// Centralised brand + business data for Shiva Prints & Packs
import {
  CreditCard, BookOpen, FileText, Image as ImageIcon, FileSignature, Files,
  Flag, Layers, Package, Box, Tag, Sticker, ShoppingBag, PenTool, Palette,
  Shirt, Coffee, HardHat, Gift, Building2, Rocket, Store, Factory,
  ShoppingCart, GraduationCap, Utensils, HeartPulse,
  Expand, Presentation, Scroll, Frame, Megaphone, Square
} from 'lucide-react';

export const CONTACT = {
  phoneDisplay: '+91 85274 64869',
  phoneRaw: '+918527464869',
  tel: 'tel:+918527464869',
  whatsapp: 'https://wa.me/918527464869?text=Hi%20Shiva%20Prints%20%26%20Packs%2C%20I%27d%20like%20an%20enquiry.',
  location: 'Delhi, India',
  coverage: 'Serving Pan India',
  email: 'shivaprintsnpacks@gmail.com',
  mailto: 'mailto:shivaprintsnpacks@gmail.com',
  brand: 'Shiva Prints & Packs',
};

export const SERVICE_CATEGORIES = [
  {
    id: 'printing',
    title: 'Printing Solutions',
    tagline: 'Sharp, colour-accurate print for every business need.',
    icon: FileText,
    items: [
      { name: 'Business Cards', desc: 'Premium finishes that make a lasting first impression.', icon: CreditCard },
      { name: 'Brochures', desc: 'Multi-fold brochures that tell your brand story clearly.', icon: BookOpen },
      { name: 'Flyers', desc: 'High-impact single sheets for promotions and campaigns.', icon: FileText },
      { name: 'Posters', desc: 'Large-format posters with vivid, durable colour.', icon: ImageIcon },
      { name: 'Letterheads', desc: 'Professional stationery that reinforces credibility.', icon: FileSignature },
      { name: 'Catalogs', desc: 'Bound catalogs that showcase your full product range.', icon: Files },
    ],
  },
  {
    id: 'large-format',
    title: 'Large Format Printing',
    tagline: 'High-impact, oversized print for events, retail and outdoor branding.',
    icon: Expand,
    items: [
      { name: 'Flex Banners', desc: 'Durable, weather-resistant banners for promotions and events.', icon: Flag },
      { name: 'Vinyl Printing', desc: 'Vivid vinyl prints for signage, walls and surfaces.', icon: Layers },
      { name: 'Standees', desc: 'Free-standing display boards that grab attention at events.', icon: Presentation },
      { name: 'Roll-Up Stands', desc: 'Portable retractable stands, perfect for exhibitions.', icon: Scroll },
      { name: 'Backdrops', desc: 'Large branded backdrops for stages, launches and shoots.', icon: Frame },
      { name: 'Hoardings', desc: 'Outdoor hoardings built for maximum visibility and reach.', icon: Megaphone },
      { name: 'Sun Board', desc: 'Rigid sun board prints for durable indoor and outdoor displays.', icon: Square },
    ],
  },
  {
    id: 'packaging',
    title: 'Packaging Solutions',
    tagline: 'Protective, brand-forward packaging with low MOQs.',
    icon: Package,
    items: [
      { name: 'Product Boxes', desc: 'Custom rigid and folding cartons tailored to your product.', icon: Box },
      { name: 'Corrugated Boxes', desc: 'Sturdy shipping boxes engineered for safe transit.', icon: Package },
      { name: 'Labels', desc: 'Precision-cut labels in a range of materials and finishes.', icon: Tag },
      { name: 'Stickers', desc: 'Durable branded stickers for products and packaging.', icon: Sticker },
      { name: 'Pouches', desc: 'Flexible pouches ideal for food, retail and e-commerce.', icon: ShoppingBag },
    ],
  },
  {
    id: 'design',
    title: 'Design Support',
    tagline: 'Print-ready design handled in-house.',
    icon: PenTool,
    items: [
      { name: 'Logo Design', desc: 'Print-focused logo design that scales across materials.', icon: PenTool },
      { name: 'Packaging Design', desc: 'Structural and graphic design built for the shelf.', icon: Palette },
    ],
  },
  {
    id: 'merchandise',
    title: 'Corporate Merchandise',
    tagline: 'Branded merchandise for teams, gifting and events.',
    icon: Gift,
    items: [
      { name: 'T-shirts', desc: 'Custom branded apparel for teams and campaigns.', icon: Shirt },
      { name: 'Mugs', desc: 'Personalised mugs for gifting and office branding.', icon: Coffee },
      { name: 'Caps', desc: 'Embroidered and printed caps for events and staff.', icon: HardHat },
      { name: 'Corporate Gifts', desc: 'Curated gift sets that leave a premium impression.', icon: Gift },
    ],
  },
];

export const WHY_CHOOSE = [
  { stat: '10+', label: 'Years of Industry Experience' },
  { stat: 'Competitive Pricing', label: 'High-quality printing at competitive prices.' },
  { stat: 'Pan-India Network', label: 'Trusted manufacturing and logistics partners across India.' },
  { stat: 'One-Stop Partner', label: 'Complete printing, packaging and branding under one roof.' },
];

export const WHY_POINTS = [
  { title: 'One-Stop Solution', desc: 'Printing, packaging, design and merchandise handled under one roof — no juggling multiple vendors.' },
  { title: 'Flexible Production', desc: 'In-house capabilities combined with trusted manufacturing partners let us scale to any order size.' },
  { title: 'Reliable Quality', desc: 'Consistent quality control and dependable timelines you can plan your business around.' },
  { title: 'Nationwide Delivery', desc: 'Delhi-based with pan-India shipping, so distance is never a constraint.' },
];

export const PROCESS = [
  { step: '01', title: 'Consult', desc: 'Share your requirement. We understand specs, quantities and timelines.' },
  { step: '02', title: 'Design & Proof', desc: 'Our team prepares print-ready design and shares proofs for approval.' },
  { step: '03', title: 'Manufacture', desc: 'Production runs through our in-house setup and trusted partners.' },
  { step: '04', title: 'Deliver', desc: 'Quality-checked orders shipped reliably across India.' },
];

export const INDUSTRIES = [
  { name: 'Corporate Offices', icon: Building2 },
  { name: 'Startups', icon: Rocket },
  { name: 'Retail Businesses', icon: Store },
  { name: 'Manufacturers', icon: Factory },
  { name: 'E-commerce', icon: ShoppingCart },
  { name: 'Education', icon: GraduationCap },
  { name: 'Food Businesses', icon: Utensils },
  { name: 'Healthcare', icon: HeartPulse },
];

export const IMAGES = {
  heroAbstract: 'https://images.pexels.com/photos/4252522/pexels-photo-4252522.jpeg',
  packagingBoxes: 'https://images.pexels.com/photos/17260157/pexels-photo-17260157.jpeg',
  printingPress: 'https://images.pexels.com/photos/19837529/pexels-photo-19837529.jpeg',
  businessCards: 'https://images.unsplash.com/photo-1651761003916-01779607d4b4',
  brochureMockup: 'https://images.unsplash.com/photo-1663177271861-3d622a502fee',
  cityDelhi: 'https://images.pexels.com/photos/28678222/pexels-photo-28678222.jpeg',
};

export const SERVICE_OPTIONS = [
  'Printing Solutions',
  'Packaging Solutions',
  'Design Support',
  'Corporate Merchandise',
  'Not sure yet',
];

export const FAQS = [
  {
    q: 'What printing services do you offer?',
    a: 'We provide end-to-end printing, packaging, and branding solutions, including brochures, catalogues, business stationery, corporate gifting, promotional merchandise, packaging, labels, apparel printing, large-format printing, and custom marketing materials.',
  },
  {
    q: 'Do you handle bulk corporate orders?',
    a: 'Yes. We specialize in managing bulk orders for corporates, media houses, educational institutions, agencies, manufacturers, and government organizations while maintaining consistent quality and timely execution.',
  },
  {
    q: 'Do you deliver across India?',
    a: 'Yes. Through our trusted manufacturing and logistics partner network, we execute projects and deliveries across India.',
  },
  {
    q: 'Can you customize products according to our brand guidelines?',
    a: 'Absolutely. We offer fully customized printing and branding solutions, including custom sizes, materials, finishes, packaging, and branding elements that align with your brand guidelines.',
  },
  {
    q: 'What is your minimum order quantity?',
    a: 'Minimum order quantities vary depending on the product and customization requirements. We recommend the most cost-effective production option based on your project needs.',
  },
  {
    q: 'Do you provide design support?',
    a: 'Yes. We can work with your existing artwork or assist in preparing print-ready files that meet production standards.',
  },
  {
    q: 'How do I request a quotation?',
    a: 'Simply fill out our quote request form, call us, email us, or contact us via WhatsApp with your project details. Our team will review your requirements and provide a customized quotation.',
  },
  {
    q: 'Can you help us manage multiple printing requirements under one project?',
    a: 'Yes. We can coordinate multiple printing, packaging, and branding requirements as a single project, helping businesses work with one reliable partner instead of managing multiple vendors.',
  },
];
